import { ExternalLink, Stethoscope, BarChart3, Megaphone, Boxes, Users2, LineChart, Phone, Sparkles, FileDown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useState } from "react";

const SERVICES = [
  {
    Icon: Sparkles,
    title: "Estudio de rentabilidad del medicamento genérico",
    body:
      "Más del 50% de las posibilidades de dispensar EFG no se aprovechan. Medimos tu mezcla marca/genérico en unidades y € sobre el 100% del potencial. Más de 650 estudios en España: rentabilidad media cerca de 30.000 € anuales por farmacia. Sin perder clientes.",
    accent: "sky",
  },
  {
    Icon: Stethoscope,
    title: "Consultoría Farmacéutica",
    body:
      "Maximizamos tu rentabilidad con análisis detallado de operaciones, optimización de ventas con marketing farmacéutico y asesoramiento integral en gestión de personal.",
    accent: "emerald",
  },
  {
    Icon: BarChart3,
    title: "Análisis de Operaciones",
    body:
      "Estudio exhaustivo para identificar ineficiencias y oportunidades. Informe detallado con herramientas analíticas para tomar decisiones informadas.",
    accent: "amber",
  },
  {
    Icon: Megaphone,
    title: "Estrategias de Marketing Farmacéutico",
    body:
      "Campañas personalizadas para atraer y retener clientes adaptadas al mercado local de tu farmacia.",
    accent: "violet",
  },
  {
    Icon: Boxes,
    title: "Gestión de Inventario",
    body:
      "Análisis ABCD en 7 categorías. Detectamos stock inactivo sin inventario físico, ajustamos mínimos/máximos y te formamos para comprar mejor a tus proveedores rentables.",
    accent: "rose",
  },
  {
    Icon: Users2,
    title: "Gestión de Personal",
    body:
      "Sistema de dirección por objetivos en farmacia: mejora la rentabilidad, el ticket medio y la satisfacción del equipo. Distribución justa de tareas y horarios basada en datos.",
    accent: "indigo",
  },
  {
    Icon: LineChart,
    title: "Análisis Financiero",
    body:
      "Informes financieros detallados y recomendaciones estratégicas para identificar áreas de ahorro y crecimiento, con seguimiento del rendimiento económico.",
    accent: "teal",
  },
];

const ACCENT = {
  sky: "from-sky-50 text-sky-600 border-sky-100",
  emerald: "from-emerald-50 text-emerald-600 border-emerald-100",
  amber: "from-amber-50 text-amber-600 border-amber-100",
  violet: "from-violet-50 text-violet-600 border-violet-100",
  rose: "from-rose-50 text-rose-600 border-rose-100",
  indigo: "from-indigo-50 text-indigo-600 border-indigo-100",
  teal: "from-teal-50 text-teal-600 border-teal-100",
};

export default function ServicesPage() {
  const [dl, setDl] = useState(false);

  const downloadDossier = async () => {
    setDl(true);
    try {
      const res = await api.get("/brochure/dossier", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "FarmaClass_Dossier_Laboratorio.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Dossier descargado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo descargar el dossier");
    } finally {
      setDl(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="services-page">
      {/* HERO */}
      <div className="bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300 font-bold mb-3">
              Archiduque Consulting · Servicios
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Transformamos el futuro de tu farmacia
            </h1>
            <p className="mt-5 text-slate-300 text-lg max-w-xl">
              Más de <strong className="text-white">15 años de experiencia</strong> y{" "}
              <strong className="text-white">+1000 clientes satisfechos</strong>. Convertimos farmacias en
              negocios más rentables y sostenibles con servicio personalizado, análisis exhaustivo y estrategias innovadoras.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://www.archiduqueconsulting.com/servicios"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-slate-950 hover:bg-slate-100 font-bold px-6 py-3.5 rounded-full transition-colors"
                data-testid="cta-services-web"
              >
                Ver todos los servicios <ExternalLink className="h-4 w-4" />
              </a>
              <a
                href="https://api.whatsapp.com/send?phone=34609467788&text=Hola%2C%20quiero%20m%C3%A1s%20informaci%C3%B3n%20sobre%20vuestros%20servicios"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-6 py-3.5 rounded-full transition-colors"
                data-testid="cta-services-whatsapp"
              >
                <Phone className="h-4 w-4" /> WhatsApp directo
              </a>
              <button
                type="button"
                onClick={downloadDossier}
                disabled={dl}
                className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-white font-bold px-6 py-3.5 rounded-full transition-colors"
                data-testid="download-dossier"
              >
                <FileDown className="h-4 w-4" /> {dl ? "Descargando…" : "Descargar dossier (PDF)"}
              </button>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-slate-900">
              <img
                src="/img/farmaclass-services.jpg"
                alt="FarmaClass · Inteligencia y rentabilidad para farmacias que lideran"
                className="w-full h-auto object-cover block"
                loading="lazy"
                data-testid="services-hero-image"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-transparent" />
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 text-center">
              <div className="bg-white/5 border border-white/10 rounded-xl py-3">
                <div className="font-display text-2xl font-extrabold text-sky-300">+650</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">Estudios EFG</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl py-3">
                <div className="font-display text-2xl font-extrabold text-emerald-300">~30k €</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">Rentab. anual media</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl py-3">
                <div className="font-display text-2xl font-extrabold text-amber-300">15+</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">Años de experiencia</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl py-3">
                <div className="font-display text-2xl font-extrabold text-violet-300">+1.000</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">Clientes satisfechos</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SERVICIOS */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">Lo que podemos hacer por ti</h2>
        <p className="text-slate-500 max-w-2xl mb-10">
          Una farmacia es un negocio rentable si se aplican gestión y oportunidades. Estos son los servicios que ofrecemos en Archiduque Consulting.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => {
            const cls = ACCENT[s.accent];
            return (
              <a
                key={s.title}
                href="https://www.archiduqueconsulting.com/servicios"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white border border-slate-200 rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all"
                data-testid={`service-card-${i}`}
              >
                <div className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${cls} grid place-items-center mb-4`}>
                  <s.Icon className="h-6 w-6" strokeWidth={2} />
                </div>
                <div className="font-display text-lg font-bold leading-tight">{s.title}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.body}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-slate-900 group-hover:text-sky-600">
                  Más información <ExternalLink className="h-3 w-3" />
                </div>
              </a>
            );
          })}
        </div>

        {/* CTA final */}
        <div className="mt-14 bg-gradient-to-r from-slate-950 to-slate-800 text-white rounded-3xl p-10 text-center">
          <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300 font-bold">¿Interesado en conocer más?</div>
          <h3 className="font-display text-3xl sm:text-4xl font-extrabold mt-3">
            Soluciones efectivas que generan resultados tangibles.
          </h3>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a
              href="https://www.archiduqueconsulting.com/contacto/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-slate-100 font-bold px-6 py-3.5 rounded-full transition-colors"
              data-testid="cta-services-contact"
            >
              Contáctanos <ExternalLink className="h-4 w-4" />
            </a>
            <a
              href="https://www.archiduqueconsulting.com/servicios"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-bold px-6 py-3.5 rounded-full transition-colors"
            >
              Ver web completa <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
